/**
 * Compact admin status card showing how many derivatives have been
 * generated for the currently-edited car. Reads from the
 * `car_image_manifest` table populated by the upload pipeline.
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { DERIVATIVE_FORMATS, DERIVATIVE_WIDTHS, formatBytes } from "@/lib/car-image-pipeline";

interface Props {
  carId: string | null;
  /** Number of canonical images currently on the car (form.images.length). */
  imageCount: number;
}

const EXPECTED_PER_IMAGE = DERIVATIVE_FORMATS.length * DERIVATIVE_WIDTHS.length; // 9

export function CarImageManifestStatus({ carId, imageCount }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["car-image-manifest", carId],
    queryFn: async () => {
      if (!carId) return null;
      const { data, error } = await supabase
        .from("car_image_manifest")
        .select("image_id, format, width, byte_size")
        .eq("car_id", carId);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!carId,
    staleTime: 30_000,
  });

  if (!carId) return null;

  if (isLoading) {
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2.5 text-xs text-muted-foreground">
        <Loader2 size={14} className="animate-spin" /> Indlæser manifest…
      </div>
    );
  }

  const rows = data ?? [];
  const uniqueImages = new Set(rows.map((r) => r.image_id)).size;
  const totalBytes = rows.reduce((sum, r) => sum + (r.byte_size ?? 0), 0);
  const expectedRows = uniqueImages * EXPECTED_PER_IMAGE;
  const complete = rows.length === expectedRows && uniqueImages > 0;
  const missingCanonicalImages = Math.max(0, imageCount - uniqueImages);

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/30 p-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-3">
        {complete && missingCanonicalImages === 0 ? (
          <span className="inline-flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 size={14} /> Feed-klar
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle size={14} /> Mangler derivater
          </span>
        )}
        <span className="text-muted-foreground">
          {uniqueImages} {uniqueImages === 1 ? "billede" : "billeder"} • {rows.length}/{expectedRows} derivater
        </span>
        {totalBytes > 0 && (
          <span className="text-muted-foreground">{formatBytes(totalBytes)} samlet</span>
        )}
      </div>
      {missingCanonicalImages > 0 && (
        <p className="mt-1.5 text-muted-foreground">
          {missingCanonicalImages} {missingCanonicalImages === 1 ? "billede" : "billeder"} mangler manifest — sandsynligvis
          ældre uploads fra før pipelinen. Re-upload via genererer dem.
        </p>
      )}
    </div>
  );
}
